import { Helper } from '@/mixin/helper'
import WsVoip from './ws'
import { float32ToInt16PCM } from './utils'
import { alawFromPCM } from './g711a'

export default class Broadcast {
  constructor({ bufferSize, wsDomain, token }) {
    this.bufferSize = Number(bufferSize || 640)
    this.wsDomain = wsDomain
    this.audioContext = null
    this.imeis = null
    this.mediaStream = null
    this.recorder = null
    this.g711Processor = null
    this.filter = null
    this.gain = null
    this.onAudioLevelUpdate = null
    this.socket = null
    this.token = token
  }

  prepareBroadcast(imeis) {
    this.audioContext = new AudioContext()
    this.imeis = imeis
  }

  async startBroadcast({ callbackState }) {
    if (!this.audioContext || !this.imeis || this.imeis?.length === 0) {
      // eslint-disable-next-line no-console
      return console.error('audioContext or imeis not prepared')
    }
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        noiseSuppression: true,
      },
    })

    this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream)
    this.filter = this.audioContext.createBiquadFilter()
    this.gain = this.audioContext.createGain()

    this.socket = new WsVoip()
    await this.socket.connect(`${this.wsDomain}/v2/ws?token=` + this.token, {
      onopen: () => {
        // eslint-disable-next-line no-console
        console.log('WebSocket broadcast connection open ')
        const restImeis = this.imeis.slice(1)
        restImeis.forEach((imei) => {
          this.sendCommandAddVehicle(imei)
        })
        if (callbackState.onopen) {
          callbackState.onopen()
        }
      },
      onmessage: (evt) => {
        if (callbackState.onmessage) callbackState.onmessage(evt)
      },
      onerror: (evt) => {
        // eslint-disable-next-line no-console
        console.log('WebSocket broadcast connection error:', evt)
        if (callbackState.onerror) callbackState.onerror(evt)
      },
      onclose: (evt) => {
        // eslint-disable-next-line no-console
        console.log('WebSocket broadcast connection closed:', evt.code, evt.reason)
        if (callbackState.onclose) callbackState.onclose(evt)
      },
    })

    // Load both worklets
    await this.audioContext.audioWorklet.addModule('/voip/recorder.worklet.js')
    await this.audioContext.audioWorklet.addModule('/voip/g711-processor.js')

    this.recorder = new AudioWorkletNode(this.audioContext, 'recorder.worklet')
    this.g711Processor = new AudioWorkletNode(this.audioContext, 'g711-processor')

    // Anti-aliasing low-pass filter before downsampling to 8kHz
    // Cutoff at 3.8kHz to prevent aliasing (below Nyquist frequency of 4kHz)
    this.filter.type = 'lowpass'
    this.filter.frequency.value = 3800
    this.filter.Q.value = 0.707 // Butterworth response

    // Gain control
    this.gain.gain.value = 3

    // For monitoring only (optional, can be removed in production)
    this.recorder.connect(this.g711Processor)
    this.g711Processor.connect(this.audioContext.destination)
    this.recorder.connect(this.audioContext.destination)

    this.sourceNode.connect(this.gain)
    this.gain.connect(this.filter)
    this.filter.connect(this.recorder)

    // Get the actual sample rate from the audio context
    const sourceSampleRate = this.audioContext.sampleRate
    const targetSampleRate = 8000

    this.recorder.port.postMessage({
      bufferSize: this.bufferSize,
      threshold: null,
      hasNoiseCancellation: false,
      sourceSampleRate: sourceSampleRate,
      targetSampleRate: targetSampleRate,
    })

    this.recorder.port.onmessage = (event) => {
      const x = float32ToInt16PCM(event.data)
      const newData = alawFromPCM(x)
      if (newData && newData.length > 0 && this.socket) {
        this.socket.send(newData)
      }
      if (event.data.type === 'audioLevel') {
        if (this.onAudioLevelUpdate) {
          this.onAudioLevelUpdate(event.data.audioLevel)
        }
      }
    }

    this.toggleMuteMicrophone(true)

    await Helper.methods.delay(1000)
    return true
  }

  stopBroadcast() {
    if (this.onAudioLevelUpdate) this.onAudioLevelUpdate = null
    if (this.socket.workletNode) {
      this.socket.workletNode.port.onmessage = null
    }
    if (this.recorder) {
      this.recorder.disconnect()
      this.recorder = null
    }
    if (this.socket) {
      this.socket.close()
      this.socket = null
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        if (track.readyState === 'live') {
          track.stop()
        }
      })
      this.mediaStream = null
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect()
      this.sourceNode = null
    }
  }

  sendCommandAddVehicle(imei) {
    // eslint-disable-next-line no-console
    if (!this.socket) return console.error('socket is not initialized')
    const command = '~CMD:REG:' + imei
    this.socket.send(command)
  }

  sendCommandRemoveVehicle(imei) {
    // eslint-disable-next-line no-console
    if (!this.socket) return console.error('socket is not initialized')
    const command = '~CMD:UNR:' + imei
    this.socket.send(command)
  }

  addVehicleBroadcast(imeis) {
    imeis.forEach((imei) => {
      this.sendCommandAddVehicle(imei)
    })
    this.imeis = [...this.imeis, ...imeis]
  }

  removeVehicleBroadcast(imeis) {
    imeis.forEach((imei) => {
      this.sendCommandRemoveVehicle(imei)
    })
    this.imeis = this.imeis.filter((id) => !imeis.includes(id))
  }

  reconnectVehicleBroadcast(imei) {
    this.sendCommandAddVehicle(imei)
  }

  toggleMuteMicrophone(isMuted) {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        if (isMuted) {
          track.enabled = false
        } else {
          track.enabled = true
        }
      })
    }
  }
}
